import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Divider } from '@mui/material';
import { Invoice } from '@/models/invoice';
import { CompanyConfig } from '@/models/config';
import '@/components/printPage/style.css';
import '@/components/printPage/font.css';
import './bottomsection.css';

interface BottomSectionProps {
  invoice: Invoice;
  companyConfig: CompanyConfig;
}

const BottomSection: React.FC<BottomSectionProps> = ({ invoice, companyConfig }) => {
  // Format number with 2 decimal places
  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null || isNaN(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  // Convert number to words (simplified version)
  const numberToWords = (num: number | undefined | null): string => {
    if (num === undefined || num === null || isNaN(num)) {
      return 'Zero Only';
    }
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

    if (num === 0) return 'Zero';

    const convertHundreds = (n: number): string => {
      let result = '';
      if (n > 99) {
        result += ones[Math.floor(n / 100)] + ' Hundred';
        n %= 100;
        if (n > 0) result += ' ';
      }
      if (n > 19) {
        result += tens[Math.floor(n / 10)];
        n %= 10;
        if (n > 0) result += ' ' + ones[n];
      } else if (n > 9) {
        result += teens[n - 10];
      } else if (n > 0) {
        result += ones[n];
      }
      return result;
    };

    let result = '';
    let thousandIndex = 0;

    while (num > 0) {
      const group = num % 1000;
      if (group !== 0) {
        const groupWords = convertHundreds(group);
        if (thousandIndex > 0) {
          result = groupWords + ' ' + thousands[thousandIndex] + (result ? ' ' + result : '');
        } else {
          result = groupWords;
        }
      }
      num = Math.floor(num / 1000);
      thousandIndex++;
    }

    return result + ' Only';
  };

  return (
    <Box className="print-bottom-section">
      {/* Footer - Two Column Layout */}
      <Box className="print-footer">
        {/* Left Column - Legal Information and Amount in Words */}
        <Box className="print-footer-left">
          {/* Amount in Words */}
          <Box className="print-amount-words">
            <Typography className="print-amount-label print-text-base print-font-primary">
              Rupees in words :
            </Typography>
            <Typography className="print-amount-text print-text-base print-font-primary">
              Rupees {numberToWords(invoice.gross_amount || 0)}
            </Typography>
          </Box>

          <Divider className="print-section-divider" />

          {/* Payment Information */}
          <Box className="print-payment-info">
            <Typography className="print-payment-item print-text-base print-font-primary">
              Payment to be deposited : {companyConfig.name}
            </Typography>
            <Typography className="print-payment-item print-text-base print-font-primary">
              GST ID : {companyConfig.gstin}
            </Typography>
            <Typography className="print-payment-item print-text-base print-font-primary">
              Transport : {invoice.transport || 'BAKSHI'}
            </Typography>
          </Box>

          <Divider className="print-section-divider" />

          {/* Terms and Conditions */}
          <Box className="print-terms-conditions">
            <Typography className="print-terms-title print-text-base print-font-primary print-font-semibold">
              Terms and Conditions:
            </Typography>
            <Typography className="print-terms-item print-text-xs print-font-primary">
              1. Payment within 30 Days
            </Typography>
            <Typography className="print-terms-item print-text-xs print-font-primary">
              2. Subject to Thane Jurisdiction.
            </Typography>
            <Typography className="print-terms-item print-text-xs print-font-primary">
              3. The buyer is request to check the goods as per particulars given above, any Discrepancy to reported within 24 hours of receiving the goods.
            </Typography>
            <Typography className="print-terms-item print-text-xs print-font-primary">
              4. Payment if delayed Interest 18% will be changes.
            </Typography>
          </Box>

          <Divider className="print-section-divider" />

          {/* Legal Declaration */}
          <Box className="print-legal-declaration">
            <Typography className="print-declaration-text print-text-xs print-font-primary">
              I/We hereby certify that my/our registration certificate under the CGST Act. 2017 is in force on the date on which the sale of goods specified In this tax invoice is made by me/us and that the transaction of sale covered by this tax invoice has been affected by me/us and it shall be accounted for in the turnover of sales while billing my/our return and due tax, if any payable on the sale has been paid or shall be paid.
            </Typography>
          </Box>
        </Box>

        {/* Vertical Separator */}
        <Box className="print-vertical-separator" />

        {/* Right Column - Final Calculations and Signature Area */}
        <Box className="print-footer-right">
          {/* Financial Summary Table */}
          <TableContainer component={Paper} className="print-financial-summary-table">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">Bill Amount</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.bill_amount || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">Discount</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.discount || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">CGST :</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.cgst || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">SGST :</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.sgst || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">IGST :</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.igst || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="print-summary-label print-text-base print-font-primary">Round Off</TableCell>
                  <TableCell className="print-summary-value print-text-base print-font-primary print-text-right">
                    {formatNumber(invoice.round_off || 0)}
                  </TableCell>
                </TableRow>
                <TableRow className="print-net-amount-row">
                  <TableCell className="print-net-amount-label print-text-base print-font-primary print-font-bold">
                    Net Amount
                  </TableCell>
                  <TableCell className="print-net-amount-value print-text-base print-font-primary print-font-bold print-text-right">
                    {formatNumber(invoice.gross_amount || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Signature Area */}
          <Box className="print-signature-area">
            <Typography className="print-signature-company print-text-base print-font-primary print-text-center">
              For {companyConfig.name}
            </Typography>
            <Box className="print-signature-spacer" />
            <Typography className="print-signature-title print-text-base print-font-primary print-text-center">
              Authorised Signatory
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BottomSection;
